import classNames from 'classnames';
import { createPortal } from 'react-dom';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { UiButton } from 'src/components/UI/Button';
import { useRefClickOutside } from 'src/utils/hooks/useRefClickOutside';
import { ChildrenPropType } from 'src/utils/types';
import { DOM_ELEMENT_FOR_PORTALS } from 'src/config';
import styles from './styles.module.scss';

type Props = {
  children: ChildrenPropType;

  simple?: boolean;
  raw?: boolean;
  iconTrigger?: boolean;
  itemClickClose?: boolean;
  prefixChildren?: ReactNode;
  usePortal?: boolean;
  menuClassName?: string;
};

export const GridActions: React.FC<Props> = ({
  simple,
  children,
  raw = false,
  iconTrigger,
  itemClickClose,
  prefixChildren,
  usePortal,
  menuClassName,
}: Props) => {
  const [show, setShow] = useState(false);

  const setShowMemo = useCallback(
    (v: boolean) => () => {
      setShow(v);
    },
    []
  );

  const ref = useRefClickOutside<HTMLDivElement>(show, setShowMemo(false));

  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const dropdownMenuRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (show && !usePortal) {
      const parent = menuContainerRef.current?.parentElement;

      if (parent && parent.classList.contains('adjust-menu-direction')) {
        const menuBottomPosition = (dropdownMenuRef.current?.getBoundingClientRect().bottom || 0) + window.scrollY;

        /** Drop up when menu position more screen max height */
        if (menuBottomPosition > document.body.scrollHeight) {
          parent.classList.add('dropup');
        }
      }
    }
  }, [show, usePortal]);

  useEffect(() => {
    /** function to change menu position when using usePortal property is active */
    const changePosition = () => {
      const menuContainer = menuContainerRef.current;

      if (menuContainer) {
        const { right: containerRight, top, bottom, height: containerHeight } = menuContainer.getBoundingClientRect();

        const containerBottom = bottom || 0;
        const containerTop = top || 0;

        const dropdownMenu = dropdownMenuRef.current;

        if (dropdownMenu) {
          /** dropdown menu must always be on the right relative to the 3-dot menu icon */
          const dropDownMenuLeft = containerRight - (dropdownMenu.getBoundingClientRect().width || 0);

          const dropDownMenuHeight = dropdownMenu.getBoundingClientRect().height || 0;

          dropdownMenu.style.setProperty('left', `${dropDownMenuLeft}px`);

          /** Drop up when menu position more screen max height */
          if (containerBottom + dropDownMenuHeight + window.scrollY > document.body.scrollHeight) {
            dropdownMenu.style.setProperty('top', `${containerTop - dropDownMenuHeight}px`);
          } else {
            dropdownMenu.style.setProperty('top', `${containerTop + containerHeight}px`);
          }
        }
      }
    };

    /** fix 3-dot menu visible */
    const toggleMenuVisible = (show: boolean) => {
      const groupContainer = menuContainerRef.current?.closest('.btn-group.pull-right');

      if (!groupContainer) {
        return;
      }
      if (show) {
        (groupContainer as HTMLDivElement).style.setProperty('opacity', '1');
      } else {
        (groupContainer as HTMLDivElement).style.removeProperty('opacity');
      }
    };

    if (usePortal) {
      if (show) {
        toggleMenuVisible(true);

        changePosition();

        document.addEventListener('scroll', changePosition);
      } else {
        toggleMenuVisible(false);
        document.removeEventListener('scroll', changePosition);
      }
    }

    return () => {
      toggleMenuVisible(false);
      document.removeEventListener('scroll', changePosition);
    };
  }, [show, usePortal]);

  useEffect(() => {
    /** handle outer click to close the actions */
    const handleClick = (event: MouseEvent) => {
      if (event.target && !(menuContainerRef.current || ref.current)?.contains(event.target as HTMLDivElement)) {
        setShow(false);
      }
    };

    if (show) {
      window.addEventListener('click', handleClick);
    } else {
      window.removeEventListener('click', handleClick);
    }

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [ref, show]);

  const uiComponent = (
    <ul
      role="presentation"
      ref={dropdownMenuRef}
      className={classNames('dropdown-menu', { [styles.fixed]: usePortal }, menuClassName)}
      onClick={itemClickClose ? setShowMemo(false) : undefined}
    >
      {children}
    </ul>
  );

  const innerComponents = (
    <>
      {prefixChildren}

      {iconTrigger ? (
        <i role="presentation" className="material-icons" onClick={setShowMemo(!show)}>
          more_vert
        </i>
      ) : (
        <UiButton tag="a" className="dropdown-toggle" onClick={setShowMemo(!show)}>
          <i className="material-icons">more_vert</i>
        </UiButton>
      )}

      {show && (usePortal ? createPortal(uiComponent, DOM_ELEMENT_FOR_PORTALS) : uiComponent)}
    </>
  );

  return raw ? (
    <div className={classNames({ open: show })} ref={menuContainerRef}>
      {innerComponents}
    </div>
  ) : (
    <div className={simple ? 'simple-actions' : 'btn-group'} ref={ref}>
      <div className={classNames({ open: show, 'buttons-container': !simple })}>{innerComponents}</div>
    </div>
  );
};
